import type { Car } from "@views/Upholstery/types"
import { create } from "zustand"
import { createJSONStorage, devtools, persist } from "zustand/middleware"
import type { PdmVehicleCatalogStats } from "../pdm/deriveVehicleCatalogFromPdm"

/** Bump when persisted catalog shape or derivation rules change. */
export const PDM_VEHICLE_CATALOG_CACHE_VERSION = 5

/** Drop persisted picker data older than this (ms). */
const CACHE_TTL_MS = 24 * 60 * 60 * 1000

const PERSIST_STORAGE_KEY = "upholstery-pdm-vehicle-catalog"

export interface PdmVehicleCatalogState {
  pickerCars: Car[]
  loading: boolean
  error: string | null
  stats: PdmVehicleCatalogStats | null
  cachedAt: number | null
  actions: {
    setPickerCars: (cars: Car[], stats: PdmVehicleCatalogStats) => void
    setLoading: (loading: boolean) => void
    setError: (error: string | null) => void
    reset: () => void
  }
}

const initialState = {
  pickerCars: [] as Car[],
  loading: false,
  error: null as string | null,
  stats: null as PdmVehicleCatalogStats | null,
  cachedAt: null as number | null,
}

type PersistedCatalogSlice = Pick<
  PdmVehicleCatalogState,
  "pickerCars" | "stats" | "cachedAt"
>

function isPersistedCatalogFresh(cachedAt: number | null): boolean {
  if (cachedAt === null) {
    return false
  }
  return Date.now() - cachedAt < CACHE_TTL_MS
}

export const usePdmVehicleCatalogStore = create<PdmVehicleCatalogState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,
        actions: {
          setPickerCars: (pickerCars, stats) =>
            set({
              pickerCars,
              stats,
              loading: false,
              error: null,
              cachedAt: Date.now(),
            }),
          setLoading: (loading) => set({ loading }),
          setError: (error) => set({ error, loading: false }),
          reset: () => set(initialState),
        },
      }),
      {
        name: PERSIST_STORAGE_KEY,
        version: PDM_VEHICLE_CATALOG_CACHE_VERSION,
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          pickerCars: state.pickerCars,
          stats: state.stats,
          cachedAt: state.cachedAt,
        }),
        onRehydrateStorage: () => (state, rehydrateError) => {
          if (rehydrateError || !state) {
            return
          }

          const persisted = state as PersistedCatalogSlice
          if (
            persisted.pickerCars.length > 0 &&
            !isPersistedCatalogFresh(persisted.cachedAt)
          ) {
            usePdmVehicleCatalogStore.setState({
              pickerCars: [],
              stats: null,
              cachedAt: null,
            })
          }
        },
      }
    ),
    { name: "PdmVehicleCatalogStore" }
  )
)

export function clearPersistedPdmVehicleCatalog(): void {
  void usePdmVehicleCatalogStore.persist.clearStorage()
}
